import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Zap, Plus, Trash2, Pencil, RefreshCw, CheckCircle, XCircle } from 'lucide-react'

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` })
const jsonHeaders = () => ({ ...authHeader(), 'Content-Type': 'application/json' })

interface LiveEventSource {
  id: string
  name: string
  base_url: string
  username: string
  password: string
  category_match: string
  category_id?: string
  enabled: boolean
  last_synced_at?: string
  last_error?: string
  event_count: number
}

interface LiveEvent {
  stream_id: string
  name: string
  channel_number?: number
  icon?: string
  source_id: string
  slot: number
  active: boolean
}

interface SourceForm {
  name: string
  base_url: string
  username: string
  password: string
  category_match: string
  category_id: string
  enabled: boolean
}

const emptyForm: SourceForm = {
  name: '',
  base_url: '',
  username: '',
  password: '',
  category_match: '',
  category_id: '',
  enabled: true,
}

async function fetchSources(): Promise<{ sources: LiveEventSource[] }> {
  const res = await fetch('/api/live-events/sources', { headers: authHeader() })
  if (!res.ok) throw new Error('Failed to load sources')
  return res.json()
}

async function fetchEvents(): Promise<{ events: LiveEvent[]; total: number }> {
  const res = await fetch('/api/live-events', { headers: authHeader() })
  if (!res.ok) throw new Error('Failed to load events')
  return res.json()
}

interface Category { id: string; name: string; type: string; is_active: boolean }

async function fetchCategories(): Promise<Category[]> {
  const res = await fetch('/api/categories?type=live&limit=1000', { headers: authHeader() })
  if (!res.ok) throw new Error('Failed to load categories')
  return res.json()
}

export default function LiveEvents() {
  const queryClient = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState<LiveEventSource | null>(null)
  const [form, setForm] = useState<SourceForm>(emptyForm)
  const [testResult, setTestResult] = useState<Record<string, string>>({})

  const { data: sourcesData } = useQuery({ queryKey: ['le-sources'], queryFn: fetchSources })
  const { data: categories = [] } = useQuery({ queryKey: ['categories', 'live'], queryFn: fetchCategories })
  const { data: eventsData, isLoading: eventsLoading } = useQuery({
    queryKey: ['le-events'],
    queryFn: fetchEvents,
    refetchInterval: 60 * 1000,
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['le-sources'] })
    queryClient.invalidateQueries({ queryKey: ['le-events'] })
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body = JSON.stringify(form)
      const url = editing ? `/api/live-events/sources/${editing.id}` : '/api/live-events/sources'
      const method = editing ? 'PUT' : 'POST'
      const res = await fetch(url, { method, headers: jsonHeaders(), body })
      if (!res.ok) {
        const d = await res.json().catch(() => ({}))
        throw new Error(d.error || 'Failed to save source')
      }
    },
    onSuccess: () => {
      setShowModal(false)
      setEditing(null)
      setForm(emptyForm)
      invalidate()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/live-events/sources/${id}`, { method: 'DELETE', headers: authHeader() })
      if (!res.ok) throw new Error('Failed to delete source')
    },
    onSuccess: invalidate,
  })

  const refreshMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/live-events/refresh', { method: 'POST', headers: authHeader() })
      if (!res.ok) throw new Error('Refresh failed')
      return res.json()
    },
    onSuccess: invalidate,
  })

  const testMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/live-events/sources/${id}/test`, { method: 'POST', headers: authHeader() })
      const d = await res.json().catch(() => ({}))
      return { id, ...d }
    },
    onSuccess: (d: { id: string; ok?: boolean; event_count?: number; error?: string }) => {
      setTestResult((prev) => ({
        ...prev,
        [d.id]: d.ok ? `OK — ${d.event_count} events` : `Error: ${d.error || 'unknown'}`,
      }))
    },
  })

  const openCreate = () => {
    setEditing(null)
    setForm(emptyForm)
    setShowModal(true)
  }

  const openEdit = (s: LiveEventSource) => {
    setEditing(s)
    setForm({
      name: s.name,
      base_url: s.base_url,
      username: s.username,
      password: '',
      category_match: s.category_match || '',
      category_id: s.category_id || '',
      enabled: s.enabled,
    })
    setShowModal(true)
  }

  const sources = sourcesData?.sources || []
  const events = (eventsData?.events || []).slice().sort((a, b) => (a.channel_number || 0) - (b.channel_number || 0))
  const sourceName = (id: string) => sources.find((s) => s.id === id)?.name || id
  const categoryName = (id?: string) => categories.find((category) => category.id === id)?.name || 'Live Events'

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Zap className="w-6 h-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Live Events</h1>
            <p className="text-sm text-muted-foreground">
              Rotating LIVE EVENTS slots from your Xtream panels. Each slot's stream updates automatically.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refreshMutation.mutate()}
            disabled={refreshMutation.isPending}
            className="btn-secondary flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${refreshMutation.isPending ? 'animate-spin' : ''}`} />
            Refresh now
          </button>
          <button onClick={openCreate} className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Add Source
          </button>
        </div>
      </div>

      {/* Sources */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold">Sources</h2>
        {sources.length === 0 && (
          <div className="text-sm text-muted-foreground border border-border rounded-lg p-4">
            No sources yet. Add an Xtream panel (base URL + username + password) to start pulling LIVE EVENTS.
          </div>
        )}
        {sources.map((s) => (
          <div key={s.id} className="bg-card border border-border rounded-lg p-4 flex items-center justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium">{s.name}</span>
                {s.enabled ? (
                  <span className="text-xs text-green-500">enabled</span>
                ) : (
                  <span className="text-xs text-muted-foreground">disabled</span>
                )}
              </div>
              <div className="text-xs text-muted-foreground truncate">{s.base_url}</div>
              <div className="text-xs text-muted-foreground">Destination: {categoryName(s.category_id)}</div>
              <div className="text-xs text-muted-foreground">
                {s.event_count} events{s.last_error ? ` · last error: ${s.last_error}` : ''}
              </div>
              {testResult[s.id] && <div className="text-xs mt-1">{testResult[s.id]}</div>}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={() => testMutation.mutate(s.id)}
                disabled={testMutation.isPending}
                className="btn-secondary text-xs px-2 py-1"
              >
                Test
              </button>
              <button onClick={() => openEdit(s)} className="p-2 hover:bg-accent rounded" title="Edit">
                <Pencil className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  if (confirm(`Delete source "${s.name}"?`)) deleteMutation.mutate(s.id)
                }}
                disabled={deleteMutation.isPending}
                className="p-2 text-red-500 hover:bg-red-500/10 rounded"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Current events */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold">Current Events ({events.length})</h2>
        {eventsLoading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : events.length === 0 ? (
          <div className="text-sm text-muted-foreground border border-border rounded-lg p-4">
            No active events. They appear here once a source is configured and a slot has a live event.
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-accent/50 text-left">
                <tr>
                  <th className="px-3 py-2 w-16">Ch #</th>
                  <th className="px-3 py-2">Event</th>
                  <th className="px-3 py-2 w-20">Slot</th>
                  <th className="px-3 py-2">Source</th>
                  <th className="px-3 py-2 w-20">Status</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.stream_id} className="border-t border-border">
                    <td className="px-3 py-2 text-muted-foreground">{e.channel_number ?? '—'}</td>
                    <td className="px-3 py-2 flex items-center gap-2">
                      {e.icon && <img src={e.icon} alt="" className="w-6 h-6 rounded object-cover" />}
                      <span>{e.name}</span>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{e.slot}</td>
                    <td className="px-3 py-2 text-muted-foreground">{sourceName(e.source_id)}</td>
                    <td className="px-3 py-2">
                      {e.active ? (
                        <span className="inline-flex items-center gap-1 text-green-500">
                          <CheckCircle className="w-4 h-4" /> live
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-muted-foreground">
                          <XCircle className="w-4 h-4" /> off
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg border border-border w-full max-w-md p-6 space-y-4">
            <h3 className="text-lg font-semibold">{editing ? 'Edit Source' : 'Add Source'}</h3>
            <div className="space-y-4">
              <div>
                <label className="form-label">Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., ETV"
                  className="input-field"
                />
              </div>
              <div>
                <label className="form-label">Base URL</label>
                <input
                  type="text"
                  value={form.base_url}
                  onChange={(e) => setForm({ ...form, base_url: e.target.value })}
                  placeholder="http://etvserv.xyz:55337"
                  className="input-field"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Scheme, host and port only — streams become {`{base}/live/{user}/{pw}/{id}.ts`}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Username</label>
                  <input
                    type="text"
                    value={form.username}
                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="form-label">Password</label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder={editing ? '(unchanged)' : ''}
                    className="input-field"
                  />
                </div>
              </div>
              <div>
                <label className="form-label">Destination category</label>
                <select
                  value={form.category_id}
                  onChange={(e) => setForm({ ...form, category_id: e.target.value })}
                  className="input-field"
                >
                  <option value="">Live Events (default)</option>
                  {categories.filter((category) => category.is_active).map((category) => (
                    <option key={category.id} value={category.id}>{category.name}</option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  Live event channels will be created and kept in this server category.
                </p>
              </div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                  className="w-4 h-4 rounded border-border"
                />
                <span className="text-sm">Enabled</span>
              </label>
              {saveMutation.isError && (
                <p className="text-sm text-red-500">{(saveMutation.error as Error).message}</p>
              )}
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-border">
              <button
                onClick={() => {
                  setShowModal(false)
                  setEditing(null)
                  setForm(emptyForm)
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => saveMutation.mutate()}
                disabled={
                  saveMutation.isPending ||
                  !form.name ||
                  !form.base_url ||
                  !form.username ||
                  (!editing && !form.password)
                }
                className="btn-primary"
              >
                {saveMutation.isPending ? 'Saving…' : editing ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
